import {
  Avatar,
  Button,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import { ArrowLeft16Regular, Settings16Regular } from "@fluentui/react-icons";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { AIMS_URL, PROJECT_IQ_API_BASE } from "config";
import { Constants } from "common/constants";
import { designTokens } from "theme/customTheme";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { UserProfileMenu } from "components/userProfileMenu/UserProfileMenu";

import { useStyles } from "./SidebarFooter.styles";
import { ISidebarFooterProps } from "./SidebarFooter.types";

const ACCEPTABLE_USE_POLICY_PATH = "/acceptable-use-policy";

export const SidebarFooter = ({
  isVisuallyExpanded,
  currentUser,
  isUserMenuOpen,
  onUserMenuOpenChange,
}: ISidebarFooterProps): JSX.Element => {
  const styles = useStyles();

  const isAdmin: boolean =
    currentUser?.roles?.includes(
      Constants.PROJECT_IQ_ADMINS_GROUP_NAME_VALUE,
    ) === true ||
    currentUser?.roles?.includes(Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE) ===
      true;

  const navigate = useNavigate();

  const [showLogoutDialog, setShowLogoutDialog] = useState<boolean>(false);
  const [showIssueDialog, setShowIssueDialog] = useState<boolean>(false);
  const userMenuAreaRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const closeUserMenu = useCallback((): void => {
    onUserMenuOpenChange(false);
  }, [onUserMenuOpenChange]);

  const focusMenuTrigger = useCallback((): void => {
    menuTriggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        userMenuAreaRef.current &&
        !userMenuAreaRef.current.contains(e.target as Node)
      ) {
        closeUserMenu();
      }
    };
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeUserMenu();
        focusMenuTrigger();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isUserMenuOpen, closeUserMenu, focusMenuTrigger]);

  const handleUserMenuAreaBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>): void => {
      const nextFocusedElement = e.relatedTarget as Node | null;
      if (
        userMenuAreaRef.current &&
        nextFocusedElement &&
        userMenuAreaRef.current.contains(nextFocusedElement)
      ) {
        return;
      }
      closeUserMenu();
    },
    [closeUserMenu],
  );

  const handleBackToAims = () => {
    window.open(AIMS_URL || "/", "_blank", "noopener,noreferrer");
  };

  const handleAdmin = () => {
    navigate("/admin");
  };

  const handleToggleUserMenu = useCallback((): void => {
    onUserMenuOpenChange(!isUserMenuOpen);
  }, [isUserMenuOpen, onUserMenuOpenChange]);

  const handleLogoutConfirmed = useCallback(async (): Promise<void> => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = `${PROJECT_IQ_API_BASE}/logout/`;
  }, []);

  const handleLogoutDialogDismiss = useCallback((): void => {
    setShowLogoutDialog(false);
    focusMenuTrigger();
  }, [focusMenuTrigger, setShowLogoutDialog]);

  const handleReportAnIssueConfirmed = useCallback((): void => {
    window.open(Constants.ISSUE_URL_MAILTO, "_blank");
    setShowIssueDialog(false);
    focusMenuTrigger();
  }, [focusMenuTrigger, setShowIssueDialog]);

  const handleIssueDialogDismiss = useCallback((): void => {
    setShowIssueDialog(false);
    focusMenuTrigger();
  }, [focusMenuTrigger, setShowIssueDialog]);

  const handleAcceptableUsePolicy = useCallback((): void => {
    closeUserMenu();
    focusMenuTrigger();
    window.open(ACCEPTABLE_USE_POLICY_PATH, "_blank", "noopener,noreferrer");
  }, [closeUserMenu, focusMenuTrigger]);

  const hasAdminButton: boolean = currentUser !== null && isAdmin;

  return (
    <div className={styles.sidebarFooter}>
      <div
        className={mergeClasses(
          styles.actionRow,
          !isVisuallyExpanded && styles.actionRowCollapsed,
        )}
      >
        {hasAdminButton && (
          <>
            {!isVisuallyExpanded ? (
              <Tooltip content="Admin" relationship="label" positioning="after">
                <Button
                  appearance="transparent"
                  className={mergeClasses(
                    styles.backToAims,
                    styles.backToAimsCollapsed,
                  )}
                  icon={<Settings16Regular />}
                  onClick={handleAdmin}
                  aria-label="Admin"
                />
              </Tooltip>
            ) : (
              <Button
                appearance="transparent"
                className={styles.backToAims}
                onClick={handleAdmin}
              >
                <span className={styles.inlineIcon}>
                  <Settings16Regular />
                </span>
                Admin
              </Button>
            )}
          </>
        )}
        {!isVisuallyExpanded ? (
          <Tooltip
            content="Back to AIMS"
            relationship="label"
            positioning="after"
          >
            <Button
              appearance="transparent"
              className={mergeClasses(
                styles.backToAims,
                styles.backToAimsCollapsed,
              )}
              icon={<ArrowLeft16Regular />}
              onClick={handleBackToAims}
              aria-label="Back to AIMS"
            />
          </Tooltip>
        ) : (
          <Button
            appearance="transparent"
            className={styles.backToAims}
            onClick={handleBackToAims}
          >
            ← AIMS
          </Button>
        )}
      </div>
      {currentUser && (
        <div
          ref={userMenuAreaRef}
          className={styles.userMenuArea}
          onBlur={handleUserMenuAreaBlur}
        >
          {isUserMenuOpen && (
            <div className={styles.userMenuPopup}>
              <UserProfileMenu
                currentUser={currentUser}
                onLogout={() => {
                  closeUserMenu();
                  setShowLogoutDialog(true);
                }}
                onReportAnIssue={() => {
                  closeUserMenu();
                  setShowIssueDialog(true);
                }}
                onAcceptableUsePolicy={handleAcceptableUsePolicy}
              />
            </div>
          )}
          <Button
            ref={menuTriggerRef}
            appearance="transparent"
            className={mergeClasses(
              styles.userProfileTrigger,
              !isVisuallyExpanded && styles.userProfileTriggerCollapsed,
            )}
            onClick={handleToggleUserMenu}
            aria-haspopup="true"
            aria-expanded={isUserMenuOpen}
            aria-label={`${currentUser.displayName}, user profile menu`}
          >
            <Avatar
              name={currentUser.displayName}
              size={28}
              initials={{
                style: {
                  backgroundColor: designTokens.accent[500],
                  color: designTokens.neutral[50],
                },
              }}
            />
            <div
              className={mergeClasses(
                styles.userInfo,
                !isVisuallyExpanded && styles.userInfoHidden,
              )}
            >
              <span className={styles.userName}>{currentUser.displayName}</span>
              <span className={styles.userCompany}>North Highland</span>
            </div>
          </Button>
        </div>
      )}
      <ActionDialog
        isVisible={showLogoutDialog}
        title="Log Out Alert"
        message="Are you sure you want to log out?"
        confirmationMessage="Yes"
        dismissMessage="No"
        spinnerMessage="Logging out..."
        onConfirmation={handleLogoutConfirmed}
        onDismiss={handleLogoutDialogDismiss}
      />
      <ActionDialog
        isVisible={showIssueDialog}
        title="Report an Issue"
        message={`Clicking "Continue" will open an email to our support team. For faster resolution, please describe what happened in detail, attach screenshots if possible, and include any error messages you received.`}
        confirmationMessage="Continue"
        dismissMessage="Cancel"
        onConfirmation={handleReportAnIssueConfirmed}
        onDismiss={handleIssueDialogDismiss}
      />
    </div>
  );
};
